import { useEffect, useId } from 'react';

import { type Betslip } from '@cx-modules/types';
import { pollForWindowProperty } from '@cx-modules/utils';

declare global {
  interface Window {
    __ocBetslipCallbackRegistry?: Map<string, Map<string, (...args: any[]) => void>>;
    ocBetslip?: Betslip;
  }
}

/**
 * Helper to register callbacks with window.ocBetslip (UK betslip).
 * Supports multiple module instances by maintaining a callback registry.
 * Each instance gets a unique ID and all callbacks are invoked when betslip triggers.
 */
export function useRegisterOcBetslipCallbacks(
  callbacks: Record<string, (...args: any[]) => void>,
  deps: React.DependencyList,
) {
  const instanceId = useId();

  useEffect(() => {
    let mounted = true;

    const registerCallbacks = async () => {
      // Initialize global registry if it doesn't exist
      if (!window.__ocBetslipCallbackRegistry) {
        window.__ocBetslipCallbackRegistry = new Map();
      }

      const registry = window.__ocBetslipCallbackRegistry;

      // Register this instance's callbacks in the registry
      Object.keys(callbacks).forEach(callbackName => {
        if (!registry.has(callbackName)) {
          registry.set(callbackName, new Map());
        }
        registry.get(callbackName)?.set(instanceId, callbacks[callbackName]);
      });

      // Poll for window.ocBetslip.setCallbacks to be ready
      try {
        await pollForWindowProperty('ocBetslip.setCallbacks');

        if (!mounted) return;

        // UK betslip is ready - register forwarding callbacks
        // These will call ALL registered instances
        const forwardingCallbacks: Record<string, (...args: any[]) => void> = {};

        registry.forEach((instances, callbackName) => {
          forwardingCallbacks[callbackName] = (...args: any[]) => {
            // Call all registered instances for this callback
            instances.forEach(callback => callback(...args));
          };
        });

        window.ocBetslip?.setCallbacks(forwardingCallbacks);
      } catch (error) {
        if (mounted) {
          console.warn('[useRegisterOcBetslipCallbacks] window.ocBetslip.setCallbacks never became available', error);
        }
      }
    };

    registerCallbacks();

    return () => {
      mounted = false;

      // Cleanup: remove this instance from the registry
      if (window.__ocBetslipCallbackRegistry) {
        const registry = window.__ocBetslipCallbackRegistry;
        Object.keys(callbacks).forEach(callbackName => {
          registry.get(callbackName)?.delete(instanceId);
        });
      }
    };
    // Use provided deps instead of [callbacks] to avoid re-registration on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
